'use client';

import { setStage, Stage } from '@3fourteen/core';
import { NotifiableError } from '@bugsnag/js';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { mainBugsnag, MainErrorBoundary } from 'helpers/bugsnag';
import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { SearchModal } from 'components/SearchModal';

import { AuthProvider, ProtectRoute } from '../contexts/auth';
import { HistoryProvider } from '../contexts/history';
import { ToastProvider } from '../contexts/toast';
import { IS_PRODUCTION } from '../services/environment';

setStage(IS_PRODUCTION ? Stage.prod : Stage.dev);

export default function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            mainBugsnag.notify(error as NotifiableError, (event) => {
              event.context = 'QueryCache';
              event.addMetadata("Add'l Info", {
                query,
              });
            });
          },
        }),
      })
  );

  return (
    <MainErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HistoryProvider>
          <AuthProvider>
            <ToastProvider>
              <ProtectRoute>
                {children}
                <SearchModalWrapper />
              </ProtectRoute>
            </ToastProvider>
          </AuthProvider>
        </HistoryProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </MainErrorBoundary>
  );
}

function SearchModalWrapper() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      setIsSearchOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Suspense fallback={null}>
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </Suspense>
  );
}
